import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router";
import { useSammlung } from "../plattform/lesemodelle";
import { useNetz } from "../plattform/Offline";
import { gemerkteReviere, reviereMerken, useSitzung } from "../plattform/sitzung";
import { Knopf } from "./Kachel";
import { Symbol } from "./Symbol";

/**
 * Ziele der Kopfnavigation (oberflaeche.md §3.1): [Pfad, Begriff, Symbol]; „Reviere“ nur für Jagdherren. Ob die Person
 * Jagdherr ist, steht in der Liste ihrer Reviere; bis sie gelesen ist, gilt der Merker der Anmeldung.
 */
export function useKopfziele(): Array<[string, string, string]> {
  const { uid } = useSitzung();
  const reviere = useSammlung(uid ? `personen/${uid}/reviere` : null);
  const netz = useNetz();
  const jagdherr = reviere.daten.length > 0;
  const gelesen = !reviere.laedt && netz && !reviere.fehler;
  const liste = JSON.stringify(reviere.daten.map((r) => ({ Revier: r._id, Reviername: String(r["Revier"] ?? "") })));
  useEffect(() => {
    if (uid && (jagdherr || gelesen)) reviereMerken(uid, JSON.parse(liste));
  }, [uid, jagdherr, gelesen, liste]);
  // Bis die Liste gelesen ist (und ohne Netz) gilt, was die Anmeldung gemeldet hat.
  const zeigen = jagdherr || (!gelesen && gemerkteReviere(uid).length > 0);
  return [
    ["/", "Meine Drückjagden", "drueckjagden"],
    ["/kanaele", "Kanäle", "nachricht"],
    ...(zeigen ? [["/reviere", "Reviere", "jagdherr"] as [string, string, string]] : []),
    ["/konto", "Konto", "konto"],
    ["/einstellungen", "Einstellungen", "einstellungen"],
    ["/meldungen", "Meldungen", "warteschlange"],
  ];
}

/** Ziele der Kopfnavigation, die direkt in ihrer einen Reihe stehen; „mehr“ ist die letzte Spalte (Anforderungen K101 (2)). */
export const KOPFZIELE_IN_DER_REIHE = 2;

/**
 * Kopfnavigation (oberflaeche.md §3.1, §1.6 Nr. 5 V-26; design/README §9.12, §14 G2; Referenz `kopfnav-neutral`):
 * `navigation` „Navigation“ in **einer Reihe** aus drei kompakten Spalten (Symbol über dem Begriff): die ersten Ziele als
 * `link`, zuletzt `button` „mehr“. „mehr“ öffnet die Ansicht „mehr“ (`main` „mehr“, wie in den Ansichten mit Zustandsband,
 * V-7) mit allen Zielen als `link`. Eine Reihe statt drei gibt der Liste darunter bei großer Schrift und auf Telefonen mit
 * 360 px Breite die Höhe zurück (Anforderungen K101, Bericht G1 D-1/D-2). „Reviere“ nur für Jagdherren (die Liste ihrer
 * Reviere ist nicht leer). Das aktuelle Ziel trägt `aria-current` und eine Kante.
 */
export function Kopfnavigation() {
  const ziele = useKopfziele();
  const navigate = useNavigate();
  return (
    <nav className="kopfnav-neutral" aria-label="Navigation" data-gleich="">
      {ziele.slice(0, KOPFZIELE_IN_DER_REIHE).map(([pfad, begriff, symbol]) => (
        <NavLink key={pfad} to={pfad} end={pfad === "/"} className="k k--planung" aria-label={begriff}>
          <Symbol name={symbol} />
          <span className="k__b">{begriff}</span>
        </NavLink>
      ))}
      <Knopf art="planung-zweit" onPress={() => navigate("/mehr")}>mehr</Knopf>
    </nav>
  );
}
